/*
Enhanced Vehicle Video component supporting both YouTube URLs and S3 video uploads.
YouTube URLs for general use, S3 uploads specifically for ChileAutos integration.
Autor: Assistant
Fecha: 2025-01-02
*/
import React, { useState, useRef, useEffect } from 'react'

import Vehicle from '../interfaces/Vehicle'
import useVehicleVideoPoster from '../hooks/useVehicleVideoPoster'
import useVideoUpload from '../hooks/useVideoUpload'
import { makeUrlVideoPresignedUrl } from '../hooks/connectionData'
import { getToken } from '../utils/utils'
import { isValidYouTubeUrl, extractYouTubeId } from '../utils/youtubeUtils'

import InputField from './InputField'
import { ButtonAction } from './Buttons'

interface VehicleVideoEnhancedProps {
  vehicle: Vehicle
  canModify: boolean
  onRefresh: () => void
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

const formatDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)

  return `${mins}:${secs.toString().padStart(2, '0')}`
}

const VehicleVideoEnhanced: React.FC<VehicleVideoEnhancedProps> = ({ vehicle, canModify, onRefresh }) => {
  // YouTube URL state
  const [youtubeUrl, setYoutubeUrl] = useState<string>(vehicle.videoUrl || '')
  const [youtubeInputValue, setYoutubeInputValue] = useState<string>(vehicle.videoUrl || '')
  const [youtubeError, setYoutubeError] = useState<string>('')
  const [youtubeShowPreview, setYoutubeShowPreview] = useState<boolean>(!!vehicle.videoUrl)
  const [youtubeSuccessMsg, setYoutubeSuccessMsg] = useState<string>('')

  // S3 Upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploadSuccessMsg, setUploadSuccessMsg] = useState<string>('')
  const [s3VideoUrl, setS3VideoUrl] = useState<string>('')
  const [loadingVideoUrl, setLoadingVideoUrl] = useState<boolean>(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Hooks
  const { postVideoUrl, loadingPost, errorPostVideo } = useVehicleVideoPoster()
  const {
    uploadVideo,
    deleteVideo,
    validateVideoFile,
    getVideoDuration,
    uploadProgress,
    isUploading,
    uploadError,
    deleteError,
    isDeleting,
  } = useVideoUpload()

  // Fetch presigned URL for S3 video when component mounts or vehicle changes
  useEffect(() => {
    const fetchVideoUrl = async () => {
      if (vehicle.videoURLs3) {
        setLoadingVideoUrl(true)
        try {
          const token = getToken()
          const response = await fetch(makeUrlVideoPresignedUrl(vehicle._id), {
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          })

          if (response.ok) {
            const data = await response.json()
            setS3VideoUrl(data.presignedUrl)
          } else {
            console.error('Failed to fetch presigned URL')
          }
        } catch (error) {
          console.error('Error fetching presigned URL:', error)
        } finally {
          setLoadingVideoUrl(false)
        }
      }
    }

    fetchVideoUrl()
  }, [vehicle._id, vehicle.videoURLs3])

  // YouTube URL handlers
  const handleYoutubeInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setYoutubeInputValue(e.target.value)
    setYoutubeError('')
    setYoutubeSuccessMsg('')

    if (isValidYouTubeUrl(e.target.value)) {
      setYoutubeUrl(e.target.value)
      setYoutubeShowPreview(true)
    } else {
      setYoutubeShowPreview(false)
    }
  }

  const handleYoutubeSave = async () => {
    if (!isValidYouTubeUrl(youtubeInputValue)) {
      setYoutubeError('Por favor ingresa una URL válida de YouTube.')

      return
    }

    setYoutubeError('')
    setYoutubeSuccessMsg('')

    const result = await postVideoUrl({
      vehicleId: vehicle._id,
      videoUrl: youtubeInputValue,
    })

    if (result === 'OK') {
      setYoutubeUrl(youtubeInputValue)
      setYoutubeShowPreview(true)
      setYoutubeSuccessMsg('Video de YouTube guardado exitosamente.')
      onRefresh()
    } else {
      setYoutubeError('Error al guardar el video de YouTube.')
    }
  }

  // S3 Upload handlers
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]

    if (!file) return

    const validation = validateVideoFile(file)

    if (!validation.isValid) {
      setUploadSuccessMsg('')

      // We'll show the error in the upload process
      return
    }

    setSelectedFile(file)
    setUploadSuccessMsg('')
  }

  const handleUpload = async () => {
    if (!selectedFile) return

    try {
      const duration = await getVideoDuration(selectedFile)

      if (duration > 60) {
        setUploadSuccessMsg('')

        return
      }

      const result = await uploadVideo({
        vehicleId: vehicle._id,
        file: selectedFile,
        duration: duration,
      })

      if (result?.success) {
        setUploadSuccessMsg('Video subido exitosamente para ChileAutos.')
        setSelectedFile(null)

        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
        onRefresh()
      }
    } catch (error) {
      console.error('Error getting video duration:', error)
    }
  }

  const handleDeleteS3Video = async () => {
    const success = await deleteVideo({ vehicleId: vehicle._id })

    if (success) {
      setUploadSuccessMsg('Video eliminado exitosamente.')
      setS3VideoUrl('') // Clear the video URL
      onRefresh()
    }
  }

  const youtubeVideoId = extractYouTubeId(youtubeUrl)
  const hasS3Video = !!vehicle.videoURLs3

  return (
    <div className="mt-6 space-y-8">
      {/* YouTube URL Section */}
      <div className="border-b pb-6">
        <h3 className="text-lg font-semibold mb-4 text-blue-600">Video de YouTube</h3>
        <div className="flex flex-row items-start gap-8">
          <div className="flex flex-col items-start gap-2">
            <InputField
              label="URL del video de YouTube"
              name="youtubeUrl"
              type="string"
              value={youtubeInputValue}
              handleInputChange={handleYoutubeInputChange}
              errors={youtubeError ? { youtubeUrl: youtubeError } : {}}
              disabled={!canModify}
            />
            <ButtonAction
              label="Guardar YouTube"
              handleButtonClick={handleYoutubeSave}
              disabled={!canModify || !youtubeInputValue || loadingPost}
            />
            {youtubeSuccessMsg && <div className="text-green-600 mt-2">{youtubeSuccessMsg}</div>}
            {youtubeError && <div className="text-red-600 mt-2">{youtubeError}</div>}
            {errorPostVideo && <div className="text-red-600 mt-2">{errorPostVideo.message}</div>}
          </div>

          {youtubeShowPreview && youtubeVideoId && (
            <div className="flex flex-col items-center">
              <div className="font-bold mb-2">Vista previa - YouTube</div>
              <iframe
                width="400"
                height="225"
                src={`https://www.youtube.com/embed/${youtubeVideoId}`}
                title="YouTube video preview"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          )}
        </div>
      </div>

      {/* S3 Upload Section for ChileAutos */}
      <div>
        <h3 className="text-lg font-semibold mb-4 text-green-600">Video para ChileAutos</h3>
        <div className="text-sm text-gray-600 mb-4">
          Sube un video específicamente para publicar en ChileAutos. Formatos: MP4, MOV | Máximo: 100MB, 60 segundos
        </div>

        {!hasS3Video ? (
          <div className="flex flex-col items-start gap-4">
            <div className="flex flex-col gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="video/mp4,video/quicktime"
                onChange={handleFileSelect}
                disabled={!canModify || isUploading}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />

              {selectedFile && (
                <div className="text-sm text-gray-600">
                  Archivo seleccionado: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                </div>
              )}
            </div>

            <ButtonAction
              label={isUploading ? `Subiendo... ${uploadProgress}%` : 'Subir Video'}
              handleButtonClick={handleUpload}
              disabled={!canModify || !selectedFile || isUploading}
            />

            {isUploading && (
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            )}

            {uploadError && <div className="text-red-600">{uploadError}</div>}
            {uploadSuccessMsg && <div className="text-green-600">{uploadSuccessMsg}</div>}
          </div>
        ) : (
          <div className="flex flex-row items-start gap-8">
            <div className="flex flex-col gap-2">
              <div className="text-sm text-gray-700">
                <strong>Video subido:</strong> {vehicle.videoURLs3?.originalName}
              </div>
              <div className="text-sm text-gray-600">
                Duración: {formatDuration(vehicle.videoURLs3?.duration || 0)} | Tamaño:{' '}
                {formatFileSize(vehicle.videoURLs3?.size || 0)}
              </div>
              <div className="text-sm text-gray-600">
                Subido: {new Date(vehicle.videoURLs3?.uploadedAt || '').toLocaleDateString()}
              </div>
              <div className="text-sm text-blue-600">
                <strong>URL para ChileAutos:</strong>{' '}
                {loadingVideoUrl
                  ? 'Generando...'
                  : s3VideoUrl
                    ? 'URL generada (válida por 24h)'
                    : 'Error generando URL'}
              </div>
              {s3VideoUrl && <div className="text-xs text-gray-500 break-all">{s3VideoUrl.substring(0, 100)}...</div>}

              <ButtonAction
                label={isDeleting ? 'Eliminando...' : 'Eliminar Video'}
                handleButtonClick={handleDeleteS3Video}
                disabled={!canModify || isDeleting}
              />

              {deleteError && <div className="text-red-600">{deleteError}</div>}
              {uploadSuccessMsg && <div className="text-green-600">{uploadSuccessMsg}</div>}
            </div>

            <div className="flex flex-col items-center">
              <div className="font-bold mb-2">Vista previa - ChileAutos</div>
              {loadingVideoUrl ? (
                <div className="w-400 h-225 bg-gray-200 rounded-lg flex items-center justify-center">
                  <div className="text-gray-500">Cargando video...</div>
                </div>
              ) : s3VideoUrl ? (
                <video width="400" height="225" controls preload="metadata" className="rounded-lg shadow-lg">
                  <source src={s3VideoUrl} type="video/mp4" />
                  Tu navegador no soporta el elemento de video.
                </video>
              ) : (
                <div className="w-400 h-225 bg-gray-200 rounded-lg flex items-center justify-center">
                  <div className="text-gray-500">Error cargando video</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default VehicleVideoEnhanced
